import { freeShippingThreshold } from "src/data/feeds/shipping"
import { DetailProductData } from "src/data/product-details/types"
import { ResolvedListProductData } from "src/data/product-list-resolved/types"
import { recordError } from "src/utilities/errorReporting"
import { ListProductData, ListProductVariationData } from "./types"

function isDetailProductData(
  product: ListProductData | ResolvedListProductData | DetailProductData,
): product is DetailProductData {
  const key: keyof ListProductData = "image"

  return !(key in product)
}

export function getProductImage(product: ListProductData | ResolvedListProductData | DetailProductData) {
  const image = isDetailProductData(product)
    ? product.images?.[0] ||
      product.variations?.find((variation) => variation.available)?.images?.[0] ||
      product.variations?.[0]?.images?.[0]
    : product.image

  if (!image) {
    console.warn("⚠️ Product does not have an image:", JSON.stringify({ slug: product.slug }))
  }

  return image
}

export function getProductPrice(product: ListProductData | ResolvedListProductData | DetailProductData) {
  const price = isDetailProductData(product)
    ? product.price ||
      product.variations?.find((variation) => variation.available)?.price ||
      product.variations?.[0]?.price
    : product.price

  if (typeof price !== "number") {
    console.warn("⚠️ Product does not have a images:", JSON.stringify({ slug: product.slug }))
  }

  return price || 0
}

export function getProductRegularPrice(product: ListProductData | ResolvedListProductData | DetailProductData) {
  const regularPrice = isDetailProductData(product)
    ? product.regularPrice ||
      product.variations?.find((variation) => variation.available)?.regularPrice ||
      product.variations?.[0]?.regularPrice
    : product.regularPrice

  return regularPrice
}

export function getProductDiscount(product: ListProductData | ResolvedListProductData | DetailProductData) {
  const discount = isDetailProductData(product)
    ? product.discount ||
      product.variations?.find((variation) => variation.available)?.discount ||
      product.variations?.[0]?.discount
    : product.discount

  return discount
}

export function getProductFreeShipping(product: ListProductData) {
  const price = getProductPrice(product)

  return price >= freeShippingThreshold
}

export function getProductCoupon(product: ListProductData | ResolvedListProductData | DetailProductData) {
  return isDetailProductData(product)
    ? product.coupon ||
        product.variations
          ?.filter((variation) => variation.available && variation.coupon)
          .sort((a, b) => {
            const aSavings = +(a.coupon?.text?.split(" ")[0] || 0)
            const bSavings = +(b.coupon?.text?.split(" ")[0] || 0)

            return bSavings - aSavings
          })[0]?.coupon
    : product.coupon
}

export function getProductAvailable(product: ListProductData | ResolvedListProductData | DetailProductData) {
  if (product.available) {
    return true
  }
  if ("variations" in product) {
    return product.variations?.some((variation) => variation.available) || false
  }
  return false
}

export function getProductFulfillmentFrom(product: ListProductData | DetailProductData) {
  const fulfillmentFrom =
    product.fulfillmentFrom ||
    product.variations?.find((variation) => variation.available)?.fulfillmentFrom ||
    product.variations?.[0]?.fulfillmentFrom

  return fulfillmentFrom
}

export function mapDetailProductToListProduct(product: DetailProductData): ListProductData | null {
  try {
    const hasVariations = !!product.variations?.length
    const availableDiscountedVariation = product.variations?.find(
      (variation) => variation.available && (variation.coupon || variation.regularPrice),
    )
    const availableVariation = product.variations?.find((variation) => variation.available)
    const firstVariation = product.variations?.[0]
    const variation = availableDiscountedVariation || availableVariation || firstVariation
    const resolvedProduct = hasVariations ? variation! : product

    const images = resolvedProduct.images
    const firstImage = images?.[0]
    const price = resolvedProduct.price

    if (!firstImage) {
      throw new Error(
        "❗ Detail product does not have images: " +
          JSON.stringify({
            name: product.name,
            sku: product.sku,
          }),
      )
    }
    if (!price) {
      throw new Error(
        "❗ Detail product does not have a price: " +
          JSON.stringify({
            name: product.name,
            sku: product.sku,
          }),
      )
    }

    return {
      id: product.id,
      slug: product.slug,
      sku: product.sku,
      name: product.name,
      categories: product.categories,
      slogan: product.slogan,
      image: firstImage,
      price: price,
      regularPrice: resolvedProduct.regularPrice,
      coupon: resolvedProduct.coupon,
      discount: resolvedProduct.discount,
      available: !!resolvedProduct.available,
      fulfillmentFrom: resolvedProduct.fulfillmentFrom,
      freeShipping: price >= freeShippingThreshold,
      rating: product.rating,
      variationsAreNotColors: product.variationsAreNotColors,
      variations: product.variations?.map((variation) => {
        return {
          id: variation.id,
          sku: variation.sku,
          name: variation.name,
          available: variation.available,
          tileImage: variation.tileImage,
          fulfillmentFrom: variation.fulfillmentFrom,
        } satisfies ListProductVariationData
      }),
    } satisfies ListProductData
  } catch (error) {
    recordError("❗ Error calling mapDetailProductToListProduct", error)
    return null
  }
}
